interface WorkerRequest {
  id: number;
  type: string;
  data: unknown;
}

interface WorkerResponse {
  id?: number;
  type: string;
  data: unknown;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

interface LoadOptions {
  coreURL: string;
  wasmURL: string;
}

const WORKER_URL = '/ffmpeg-worker.js?v=classic-1';

export class ClassicFfmpegClient {
  private worker: Worker | null = null;
  private nextId = 0;
  private readonly pending = new Map<number, PendingRequest>();

  constructor(private readonly progressListener: (progress: number) => void) {}

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;

    // Deliberately use a classic worker. Some Telegram Android WebViews reject
    // dynamic import() inside module workers even for same-origin HTTPS URLs.
    const worker = new Worker(WORKER_URL);
    worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
      if (data.type === 'PROGRESS') {
        const progress = Number((data.data as { progress?: unknown } | null)?.progress);
        if (Number.isFinite(progress)) this.progressListener(progress);
        return;
      }
      if (data.type === 'LOG') return;
      if (typeof data.id !== 'number') return;

      const request = this.pending.get(data.id);
      if (!request) return;
      this.pending.delete(data.id);
      if (data.type === 'ERROR') {
        const details = data.data as { message?: unknown } | null;
        request.reject(new Error(
          typeof details?.message === 'string' ? details.message : 'Видеодвижок завершился с ошибкой.',
        ));
      } else {
        request.resolve(data.data);
      }
    };
    worker.onerror = (event) => {
      event.preventDefault();
      this.failAll(new Error(event.message || 'Не удалось запустить видеодвижок.'));
      worker.terminate();
      if (this.worker === worker) this.worker = null;
    };
    worker.onmessageerror = () => {
      this.failAll(new Error('Видеодвижок вернул повреждённый ответ.'));
      worker.terminate();
      if (this.worker === worker) this.worker = null;
    };
    this.worker = worker;
    return worker;
  }

  private request<T>(type: string, data: unknown, transfer: Transferable[] = []): Promise<T> {
    const worker = this.ensureWorker();
    const id = ++this.nextId;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: (value) => resolve(value as T),
        reject,
      });
      try {
        worker.postMessage({ id, type, data } satisfies WorkerRequest, transfer);
      } catch (error) {
        this.pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private failAll(error: Error): void {
    for (const request of this.pending.values()) request.reject(error);
    this.pending.clear();
  }

  load(options: LoadOptions): Promise<boolean> {
    return this.request('LOAD', options);
  }

  exec(args: string[], timeout = -1): Promise<number> {
    return this.request('EXEC', { args, timeout });
  }

  writeFile(path: string, data: Uint8Array): Promise<boolean> {
    const fileData = data.buffer instanceof ArrayBuffer
      && data.byteOffset === 0
      && data.byteLength === data.buffer.byteLength
      ? data
      : data.slice();
    return this.request('WRITE_FILE', { path, fileData }, [fileData.buffer as ArrayBuffer]);
  }

  readFile(path: string): Promise<Uint8Array> {
    return this.request('READ_FILE', { path });
  }

  deleteFile(path: string): Promise<boolean> {
    return this.request('DELETE_FILE', { path });
  }

  terminate(reason = new Error('Видеодвижок остановлен.')): void {
    this.failAll(reason);
    this.worker?.terminate();
    this.worker = null;
  }
}
